import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import { getDataCellClass } from 'components/tables/common/simpleTableClasses';
import { formatCurrency } from 'utils';
import {
  getStoreConfigurationFlags,
} from '../../utils';
import { AuditRow } from '../../types';
import mappedIcon from '../../../../assets/images/mappedicon.svg';
import InventoryTicketMetric from './InventoryTicketMetric';
import BackstockStatusMetric from './BackstockStatusMetric';
import { AUDIT_TABLE_COLUMN_STYLES } from './auditTableColumnStyles';
import CheckMarkDisplay from '../common/CheckMarkDisplay';

interface AuditTableTotalRowProps {
  totalRow: AuditRow | null;
  selectedStore: any;
  conditionalBorderColumns: number[];
  totalRows: number;
  rowIdx: number;
  hideStockColumns?: boolean;
  showBackstockAccounting?: boolean;
  showNetVariance?: boolean;
  backstockLedgerRow?: any;
  backstockColumnOffset?: number;
  onBackstockPackMetricClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    metric: 'opening' | 'activated_to_backstock' | 'moved_from_counter' | 'returned_to_counter' | 'returned_to_lottery' | 'inventory_depletion' | 'closing'
  ) => void;
  onAuditPackMetricClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    metric: 'opening' | 'activated' | 'closing' | 'counter_sold_out'
  ) => void;
}

interface PlainMetricCellProps {
  value: number | null;
  discrepancy?: number | null;
  formatValue?: (value: number) => string;
  showCheckmarkForZeroDiscrepancy?: boolean;
}

const VARIANCE_COLUMN_STYLE: React.CSSProperties = {
  textAlign: 'right',
};
const DATE_COLUMN_STYLE: React.CSSProperties = AUDIT_TABLE_COLUMN_STYLES.date;

const formatDiscrepancyValue = (
  value: number,
  formatValue: (value: number) => string
): string => {
  if (value > 0) {
    return `+${formatValue(Math.abs(value))}`;
  }

  if (value < 0) {
    return `-${formatValue(Math.abs(value))}`;
  }

  return formatValue(0);
};

const formatSignedTicketVariance = (value: number) => {
  if (value > 0) {
    return `+${value.toLocaleString()}`;
  }

  if (value < 0) {
    return `-${Math.abs(value).toLocaleString()}`;
  }

  return null;
};

const AmountWithQuantity: React.FC<{
  amount: React.ReactNode;
  quantity: React.ReactNode;
  quantityLabel?: string;
  className?: string;
}> = ({ amount, quantity, quantityLabel = 'tickets', className = '' }) => (
  <div className={`audit-table__amount-with-quantity ${className}`.trim()}>
    <span className="audit-table__amount-with-quantity-value">{amount}</span>
    {quantity != null && (
      <span className="audit-table__amount-with-quantity-subscript">
        {quantity} {quantityLabel}
      </span>
    )}
  </div>
);

const PlainMetricCell: React.FC<PlainMetricCellProps> = ({
  value,
  discrepancy,
  formatValue = formatCurrency,
  showCheckmarkForZeroDiscrepancy = true,
}) => {
  if (value === null) {
    return <span>-</span>;
  }

  const normalizedDiscrepancy = discrepancy ?? null;
  const hasDiscrepancy = normalizedDiscrepancy !== null && normalizedDiscrepancy !== 0;
  const shouldShowCheckmark = showCheckmarkForZeroDiscrepancy
    && normalizedDiscrepancy === 0
    && value !== 0;

  return (
    <div className={`audit-table__total-metric ${!hasDiscrepancy ? 'audit-table__total-metric--single' : ''}`}>
      <div className="audit-table__total-metric-value-row">
        {shouldShowCheckmark && (
          <img src={mappedIcon} alt="matched" className="audit-table__total-metric-icon" />
        )}
        <span className="audit-table__total-metric-value">
          {formatValue(value)}
        </span>
      </div>
      {hasDiscrepancy && normalizedDiscrepancy !== null && (
        <span
          className={
            normalizedDiscrepancy > 0
              ? 'audit-table__total-metric-discrepancy audit-table__total-metric-discrepancy--positive'
              : 'audit-table__total-metric-discrepancy audit-table__total-metric-discrepancy--negative'
          }
        >
          {formatDiscrepancyValue(normalizedDiscrepancy, formatValue)}
        </span>
      )}
    </div>
  );
};

const FinalMetricCell: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) {
    return <CheckMarkDisplay value={0} title="All audits matched" />;
  }

  return (
    <div className="audit-table__total-metric audit-table__total-metric--single">
      <div className="audit-table__total-metric-value-row">
        <span
          className={
            value > 0
              ? 'audit-table__total-metric-discrepancy audit-table__total-metric-discrepancy--positive'
              : 'audit-table__total-metric-discrepancy audit-table__total-metric-discrepancy--negative'
          }
        >
          {formatDiscrepancyValue(value, formatCurrency)}
        </span>
      </div>
    </div>
  );
};

const ScratcherVarianceCell: React.FC<{
  value?: number | null;
  formatValue: (value: number) => string;
  title: string;
}> = ({ value, formatValue, title }) => {
  const variance = Number(value ?? 0);

  if (variance === 0) {
    return <CheckMarkDisplay value={0} title={`${title} matched`} />;
  }

  return (
    <span
      className={
        variance > 0
          ? 'audit-table__scratcher-variance audit-table__scratcher-variance--positive'
          : 'audit-table__scratcher-variance audit-table__scratcher-variance--negative'
      }
      title={title}
    >
      {formatValue(variance)}
    </span>
  );
};

const AuditTableTotalRow: React.FC<AuditTableTotalRowProps> = ({
  totalRow,
  selectedStore,
  conditionalBorderColumns,
  totalRows,
  rowIdx,
  hideStockColumns = false,
  showBackstockAccounting = false,
  showNetVariance = true,
  backstockLedgerRow,
  backstockColumnOffset = 0,
  onBackstockPackMetricClick,
  onAuditPackMetricClick,
}) => {
  const {
    isBothConnected,
    isBothNotConnected,
    isLottoConnectedOnly,
    isPOSConnectedOnly,
    hasPosType,
  } = getStoreConfigurationFlags(selectedStore);

  if (!totalRow) {
    return null;
  }

  const posScratcherQuantity = Number(totalRow?.pos_summary?.scratcher_quantity_sold ?? 0);
  const totalSold = Number(totalRow?.audit_summary?.total_sold ?? 0);
  const posScratcherSales = Number(totalRow?.pos_summary?.scratcher_sales ?? 0);
  const revenue = Number(totalRow?.audit_summary?.revenue ?? 0);
  const posLottoSales = Number(totalRow?.pos_summary?.lotto_sales ?? 0);
  const lottoSales = Number(totalRow?.lotto_summary?.sales ?? 0);
  const lottoPayoutsTotal = Number(totalRow?.lotto_summary?.payouts_total ?? 0);
  const freeTicketsAmount = Number(totalRow?.lotto_summary?.scratcher_free_tickets_paid_amount ?? 0);
  const posPayout = Number(totalRow?.pos_summary?.payout ?? 0);

  const scratcherQuantityVariance = totalRow?.reconciliation_variance?.scratcher?.quantity_variance;
  const scratcherRevenueVariance = totalRow?.reconciliation_variance?.scratcher?.revenue_variance;
  const lottoSalesVariance = totalRow?.reconciliation_variance?.lotto_sales_variance;
  const payoutVariance = totalRow?.reconciliation_variance?.payout_variance;
  const totalVariance = totalRow?.reconciliation_variance?.total_variance ?? 0;
  const backstockFromCounterPacks = Number(backstockLedgerRow?.moved_from_counter_packs ?? 0);
  const backstockFromCounterTickets = Number(backstockLedgerRow?.moved_from_counter_tickets ?? 0);
  const backstockActivatedPacks = Number(backstockLedgerRow?.activated_to_backstock_packs ?? 0);
  const backstockActivatedTickets = Number(backstockLedgerRow?.activated_to_backstock_tickets ?? 0);
  const backstockToCounterPacks = Number(backstockLedgerRow?.returned_to_counter_packs ?? 0);
  const backstockToCounterTickets = Number(backstockLedgerRow?.returned_to_counter_tickets ?? 0);
  const backstockReturnedToLotteryPacks = Number(backstockLedgerRow?.returned_to_lottery_packs ?? 0);
  const backstockReturnedToLotteryTickets = Number(backstockLedgerRow?.returned_to_lottery_tickets ?? 0);
  const backstockInventoryDepletionPacks = Number(backstockLedgerRow?.inventory_depletion_packs ?? 0);
  const backstockInventoryDepletionTickets = Number(backstockLedgerRow?.inventory_depletion_tickets ?? 0);

  const scratcherQtyValue = hasPosType ? posScratcherQuantity : totalSold;
  const scratcherQtyDiscrepancy = null;

  const scratcherSalesValue = hasPosType ? posScratcherSales : revenue;
  const scratcherSalesDiscrepancy = null;

  const payoutValue = isBothConnected
    ? lottoPayoutsTotal
    : isLottoConnectedOnly
      ? lottoPayoutsTotal
      : isPOSConnectedOnly
        ? posPayout
        : null;
  const stockStartColumnIndex = showBackstockAccounting ? 8 : 1;
  const backstockColumnIndex = hideStockColumns ? 1 : 4;
  const closingStockColumnIndex = stockStartColumnIndex + (showBackstockAccounting ? 5 : 2);
  const counterOpeningTickets = showBackstockAccounting ? backstockLedgerRow?.counter_opening_tickets : totalRow?.audit_summary?.opening_count;
  const counterOpeningPacks = showBackstockAccounting ? backstockLedgerRow?.counter_opening_packs : totalRow?.audit_summary?.opening_pack_count;
  const counterActivatedTickets = showBackstockAccounting ? backstockLedgerRow?.counter_activated_tickets : totalRow?.audit_summary?.newly_activated_count;
  const counterActivatedPacks = showBackstockAccounting ? backstockLedgerRow?.counter_activated_packs : totalRow?.audit_summary?.newly_activated_pack_count;
  const counterClosingTickets = showBackstockAccounting ? backstockLedgerRow?.counter_closing_tickets : totalRow?.audit_summary?.closing_count;
  const counterClosingPacks = showBackstockAccounting ? backstockLedgerRow?.counter_closing_packs : totalRow?.audit_summary?.closing_pack_count;
  const counterSoldOutTickets = backstockLedgerRow?.counter_sold_out_tickets ?? 0;
  const counterSoldOutPacks = backstockLedgerRow?.counter_sold_out_packs ?? totalRow?.audit_summary?.counter_sold_out_pack_count ?? 0;
  const counterExpectedSalesTickets = showBackstockAccounting
    ? Number(backstockLedgerRow?.counter_expected_sales_tickets ?? 0)
    : totalSold;
  const renderStockColumns = () => {
    if (hideStockColumns) {
      return null;
    }

    return (
      <>
        <TableCell className={getDataCellClass(stockStartColumnIndex, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <InventoryTicketMetric
            tickets={counterOpeningTickets}
            packs={counterOpeningPacks}
            emphasized={false}
            zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
          />
        </TableCell>
        <TableCell className={getDataCellClass(stockStartColumnIndex + 1, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <InventoryTicketMetric
            tickets={counterActivatedTickets}
            packs={counterActivatedPacks}
            emphasized={false}
            zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
          />
        </TableCell>
        {showBackstockAccounting && (
          <>
            <TableCell className={getDataCellClass(stockStartColumnIndex + 2, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
              <InventoryTicketMetric
                tickets={backstockToCounterTickets}
                packs={backstockToCounterPacks}
                emphasized={false}
                onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'returned_to_counter') : undefined}
                packTitle="Open these backstock-to-counter packs in current inventory"
                zeroPlaceholder="-"
              />
            </TableCell>
            <TableCell className={getDataCellClass(stockStartColumnIndex + 3, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
              <InventoryTicketMetric
                tickets={backstockFromCounterTickets}
                packs={backstockFromCounterPacks}
                emphasized={false}
                onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'moved_from_counter') : undefined}
                packTitle="Open these counter-to-backstock packs in current inventory"
                zeroPlaceholder="-"
              />
            </TableCell>
          </>
        )}
        {showBackstockAccounting && (
          <TableCell className={getDataCellClass(stockStartColumnIndex + 4, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={counterSoldOutTickets}
              packs={counterSoldOutPacks}
              emphasized={false}
              onPackClick={onAuditPackMetricClick ? (event) => onAuditPackMetricClick(event, 'counter_sold_out') : undefined}
              packTitle="Open these counter sold-out packs in current inventory"
              zeroPlaceholder="-"
              zeroTicketPlaceholder="-"
            />
          </TableCell>
        )}
        <TableCell className={getDataCellClass(closingStockColumnIndex, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <InventoryTicketMetric
            tickets={counterClosingTickets}
            packs={counterClosingPacks}
            emphasized={false}
            zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
          />
        </TableCell>
      </>
    );
  };

  return (
    <TableRow className="audit-table__total-row">
      <TableCell
        className={`${getDataCellClass(0, rowIdx, totalRows, conditionalBorderColumns)} audit-table__total-label-cell`}
        style={{ ...DATE_COLUMN_STYLE, textAlign: 'left' }}
      >
        <span className="audit-table__total-label">Total</span>
      </TableCell>

      {!showBackstockAccounting && renderStockColumns()}

      {!showBackstockAccounting && (
        <TableCell className={getDataCellClass(backstockColumnIndex, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <BackstockStatusMetric
            tickets={backstockLedgerRow?.closing_tickets ?? 0}
            packs={backstockLedgerRow?.closing_packs ?? 0}
            onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'closing') : undefined}
            packTitle="Activated or settled packs LottoReco still believes are in backstock and outside audited inventory"
          />
        </TableCell>
      )}

      {showBackstockAccounting && (
        <>
          <TableCell className={getDataCellClass(1, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockLedgerRow?.opening_tickets ?? 0}
              packs={backstockLedgerRow?.opening_packs ?? 0}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'opening') : undefined}
              packTitle="Open these backstock opening packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(2, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockActivatedTickets}
              packs={backstockActivatedPacks}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'activated_to_backstock') : undefined}
              packTitle="Open these activation-to-backstock packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(3, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockFromCounterTickets}
              packs={backstockFromCounterPacks}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'moved_from_counter') : undefined}
              packTitle="Open these counter-to-backstock packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(4, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockToCounterTickets}
              packs={backstockToCounterPacks}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'returned_to_counter') : undefined}
              packTitle="Open these backstock-to-counter packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(5, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockReturnedToLotteryTickets}
              packs={backstockReturnedToLotteryPacks}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'returned_to_lottery') : undefined}
              packTitle="Open these returned-to-lottery packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(6, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockInventoryDepletionTickets}
              packs={backstockInventoryDepletionPacks}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'inventory_depletion') : undefined}
              packTitle="Open these sold-out backstock packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
          <TableCell className={getDataCellClass(7, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
            <InventoryTicketMetric
              tickets={backstockLedgerRow?.closing_tickets ?? 0}
              packs={backstockLedgerRow?.closing_packs ?? 0}
              emphasized={false}
              onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, 'closing') : undefined}
              packTitle="Open these backstock closing packs in current inventory"
              zeroPlaceholder="-"
            />
          </TableCell>
        </>
      )}

      {showBackstockAccounting && renderStockColumns()}

      {!hasPosType && (
        <TableCell className={getDataCellClass(5 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <span className="audit-table__total-static-value">
            {counterExpectedSalesTickets}
          </span>
        </TableCell>
      )}

      {!hasPosType && (
        <TableCell className={getDataCellClass(6 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <span className="audit-table__total-static-value">
            {formatCurrency(revenue)}
          </span>
        </TableCell>
      )}

      {!hasPosType && (
        <TableCell className={getDataCellClass(7 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
        <PlainMetricCell
          value={scratcherQtyValue}
          discrepancy={scratcherQtyDiscrepancy}
          formatValue={(value) => value.toLocaleString()}
        />
        </TableCell>
      )}

      {hasPosType && (
        <TableCell className={getDataCellClass(5 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <AmountWithQuantity
            amount={formatCurrency(revenue)}
            quantity={totalSold.toLocaleString()}
          />
        </TableCell>
      )}

      {hasPosType && (
        <TableCell className={getDataCellClass(6 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <AmountWithQuantity
            amount={formatCurrency(posScratcherSales)}
            quantity={posScratcherQuantity.toLocaleString()}
          />
        </TableCell>
      )}

      {!hasPosType && (
        <TableCell className={getDataCellClass(8 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
        <PlainMetricCell
          value={scratcherSalesValue}
          discrepancy={scratcherSalesDiscrepancy}
          formatValue={formatCurrency}
        />
        </TableCell>
      )}

      {hasPosType && (
        <TableCell
          className={`${getDataCellClass(7 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} audit-table__variance-dollar-right-border audit-table__variance-cell-bg`}
          style={VARIANCE_COLUMN_STYLE}
        >
          <AmountWithQuantity
            amount={(
              <ScratcherVarianceCell
                value={scratcherRevenueVariance}
                formatValue={formatCurrency}
                title="Scratcher dollar variance"
              />
            )}
            quantity={formatSignedTicketVariance(Number(scratcherQuantityVariance ?? 0))}
            className={
              Number(scratcherRevenueVariance ?? 0) > 0
                ? 'audit-table__amount-with-quantity--positive'
                : Number(scratcherRevenueVariance ?? 0) < 0
                  ? 'audit-table__amount-with-quantity--negative'
                  : 'audit-table__amount-with-quantity--center'
            }
          />
        </TableCell>
      )}

      {!showBackstockAccounting && !isBothNotConnected && (
        <TableCell className={getDataCellClass((hasPosType ? 8 : 9) + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <PlainMetricCell
            value={lottoSales}
            discrepancy={null}
            formatValue={formatCurrency}
            showCheckmarkForZeroDiscrepancy={false}
          />
        </TableCell>
      )}

      {!showBackstockAccounting && hasPosType && (
        <TableCell className={getDataCellClass(9 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <PlainMetricCell
            value={posLottoSales}
            discrepancy={null}
            formatValue={formatCurrency}
            showCheckmarkForZeroDiscrepancy={false}
          />
        </TableCell>
      )}

      {!showBackstockAccounting && hasPosType && (
        <TableCell
          className={`${getDataCellClass(10 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} audit-table__variance-dollar-right-border`}
          style={VARIANCE_COLUMN_STYLE}
        >
          <ScratcherVarianceCell
            value={lottoSalesVariance}
            formatValue={formatCurrency}
            title="Lotto sales variance"
          />
        </TableCell>
      )}

      {!showBackstockAccounting && !isBothNotConnected && (
        <TableCell className={getDataCellClass((hasPosType ? 11 : 10) + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <PlainMetricCell
            value={payoutValue}
            discrepancy={null}
            formatValue={formatCurrency}
            showCheckmarkForZeroDiscrepancy={false}
          />
        </TableCell>
      )}

      {!showBackstockAccounting && hasPosType && (
        <TableCell className={getDataCellClass(12 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <PlainMetricCell
            value={freeTicketsAmount}
            discrepancy={null}
            formatValue={formatCurrency}
            showCheckmarkForZeroDiscrepancy={false}
          />
        </TableCell>
      )}

      {!showBackstockAccounting && hasPosType && (
        <TableCell className={getDataCellClass(13 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} style={VARIANCE_COLUMN_STYLE}>
          <PlainMetricCell
            value={posPayout}
            discrepancy={null}
            formatValue={formatCurrency}
            showCheckmarkForZeroDiscrepancy={false}
          />
        </TableCell>
      )}

      {!showBackstockAccounting && hasPosType && (
        <TableCell
          className={`${getDataCellClass(14 + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)} audit-table__variance-dollar-right-border`}
          style={VARIANCE_COLUMN_STYLE}
        >
          <ScratcherVarianceCell
            value={payoutVariance}
            formatValue={formatCurrency}
            title="Payout variance"
          />
        </TableCell>
      )}

      {showNetVariance && (
        <TableCell
          className={getDataCellClass((isBothNotConnected ? 9 : hasPosType ? 15 : 11) + backstockColumnOffset, rowIdx, totalRows, conditionalBorderColumns)}
          style={VARIANCE_COLUMN_STYLE}
        >
          <FinalMetricCell value={totalVariance} />
        </TableCell>
      )}
    </TableRow>
  );
};

export default AuditTableTotalRow;
